/**
 * Tiny 30-day consumption sparkline — inline SVG, no chart library.
 *
 * Draws a single polyline + a faint baseline so the operator can see
 * at-a-glance whether sales of this article are trending up / flat /
 * down. Dates whose qty is missing from ``series`` render as 0 in the
 * line; ``series`` is expected to already be daily-bucketed (one entry
 * per ``YYYY-MM-DD``).
 *
 * Renders a placeholder when ``series`` is empty so the parent can
 * pass ``[]`` while the consumption-series endpoint is still TBD
 * without conditionally mounting the component itself.
 */

import { useMemo } from "react";
import { Typography } from "antd";

const { Text } = Typography;

export interface ConsumptionPoint {
  /** ``YYYY-MM-DD``. */
  date: string;
  /** Units sold on that day. */
  qty: number;
}

interface Props {
  series: ConsumptionPoint[];
  width?: number;
  height?: number;
}

export function ConsumptionSparkline({
  series,
  width = 220,
  height = 48,
}: Props) {
  const path = useMemo(() => {
    if (series.length === 0) return null;
    // Pad to a fixed visual length so a 5-day-old article doesn't
    // render with one giant spike across the canvas. Right-align so
    // the most recent day is always at x = width.
    const max = Math.max(...series.map((p) => p.qty), 1);
    const n = series.length;
    const stride = n > 1 ? width / (n - 1) : 0;
    const points = series.map((p, i) => {
      const x = i * stride;
      // Invert y so larger qty draws higher.
      const y = height - (p.qty / max) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    return points.join(" ");
  }, [series, width, height]);

  if (series.length === 0 || path === null) {
    return (
      <div style={{ width, height, display: "flex", alignItems: "center" }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          no data yet
        </Text>
      </div>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Daily consumption (last 30 days)"
      style={{ display: "block" }}
    >
      {/* Faint baseline (0 qty) so a flat zero series is still visible. */}
      <line
        x1={0}
        x2={width}
        y1={height - 2}
        y2={height - 2}
        stroke="var(--ant-color-border, #d9d9d9)"
        strokeWidth={1}
      />
      <polyline
        points={path}
        fill="none"
        stroke="var(--ant-color-primary, #1677ff)"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default ConsumptionSparkline;
